import { Link, useNavigate } from "react-router-dom";
import { Moon, Wrench, Armchair, SprayCan, Sparkles, Route, ShoppingBag, BadgeCheck, ShieldCheck, Headset, PackageSearch, ArrowRight } from "lucide-react";
import { Reveal, PrimaryButton } from "../components/primitives";
import { useLeadModal } from "../components/LeadModalContext";
import { useCart, inr } from "./CartContext";
import { toast } from "sonner";

export const STORE_CATEGORIES = [
  { slug: "night-driving", name: "Night Driving", icon: Moon, blurb: "Glare comfort, anti-fog and clear-glass essentials." },
  { slug: "car-utility", name: "Car Utility", icon: Wrench, blurb: "Phone holders, chargers and everyday organisers." },
  { slug: "car-comfort", name: "Car Comfort", icon: Armchair, blurb: "Cushions and supports for tiring drives." },
  { slug: "car-care", name: "Car Care", icon: SprayCan, blurb: "Cloths, cleaners and interior care kits." },
  { slug: "car-style", name: "Car Style", icon: Sparkles, blurb: "Tasteful décor and fragrance for your cabin." },
  { slug: "driving-essentials", name: "Driving Essentials", icon: Route, blurb: "Emergency kits and must-have driver gear." },
];

export const catName = (slug) => STORE_CATEGORIES.find((c) => c.slug === slug)?.name || slug;

export const RecommendedBadge = () => (
  <span className="inline-flex items-center gap-1 rounded-full bg-dwc-green px-2.5 py-1 text-[9px] font-extrabold uppercase tracking-[0.15em] text-white">
    <BadgeCheck className="h-3 w-3" aria-hidden="true" /> DWC Recommended
  </span>
);

export const ProductCard = ({ product, index = 0 }) => {
  const { add } = useCart();
  const navigate = useNavigate();
  const out = (product.stock || 0) < 1;
  return (
    <Reveal delay={(index % 4) * 0.05} className="h-full">
      <article data-testid={`product-card-${product.slug}`} className="group flex h-full flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-[box-shadow,transform] duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgb(23,107,58,0.10)]">
      <Link to={`/store/product/${product.slug}`} className="relative block overflow-hidden bg-dwc-soft" aria-label={product.name}>
        <img src={(product.images || [])[0]} alt={product.name} loading="lazy" className="aspect-square w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        {product.recommended && <span className="absolute left-3 top-3"><RecommendedBadge /></span>}
        {out && <span className="absolute right-3 top-3 rounded-full bg-dwc-charcoal px-2.5 py-1 text-[9px] font-extrabold uppercase tracking-wider text-white">Out of Stock</span>}
      </Link>
      <div className="flex flex-1 flex-col p-4 sm:p-5">
        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-dwc-green">{catName(product.category)}</p>
        <Link to={`/store/product/${product.slug}`} className="mt-1.5 text-[15px] font-extrabold leading-snug tracking-tight text-dwc-charcoal transition-colors hover:text-dwc-green">
          {product.name}
        </Link>
        <p className="mt-1.5 flex-1 text-[13px] leading-relaxed text-dwc-charcoal/60 line-clamp-2">{product.short_benefit}</p>
        <div className="mt-3 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="text-lg font-black text-dwc-charcoal" data-testid={`product-price-${product.slug}`}>{inr(product.price)}</span>
          {product.mrp && product.mrp > product.price && <span className="text-xs font-semibold text-dwc-charcoal/40 line-through">{inr(product.mrp)}</span>}
          {!out && <span className="ml-auto text-[10px] font-bold uppercase tracking-wider text-dwc-green">In Stock</span>}
        </div>
        <div className="mt-4 flex flex-col gap-2 sm:flex-row">
          <button
            disabled={out}
            data-testid={`add-to-cart-${product.slug}`}
            onClick={() => { add(product); toast.success(`${product.name} added to cart`); }}
            className="flex min-h-[44px] flex-1 items-center justify-center rounded-lg border-2 border-dwc-green px-2 py-2 text-[12px] font-bold uppercase leading-[1.2] tracking-[0.02em] text-dwc-green transition-colors hover:bg-dwc-soft disabled:cursor-not-allowed disabled:opacity-40"
          >
            Add to Cart
          </button>
          <button
            disabled={out}
            data-testid={`buy-now-${product.slug}`}
            onClick={() => { add(product); navigate("/store/checkout"); }}
            className="flex min-h-[44px] flex-1 items-center justify-center rounded-lg bg-dwc-green px-2 py-2 text-[12px] font-bold uppercase leading-[1.2] tracking-[0.02em] text-white transition-colors hover:bg-dwc-green-dark disabled:cursor-not-allowed disabled:opacity-40"
          >
            Buy Now
          </button>
        </div>
      </div>
      </article>
    </Reveal>
  );
};

export const WhyShopDwc = () => (
  <section className="bg-dwc-soft py-20 md:py-24" data-testid="why-shop-section">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <h2 className="text-center text-2xl font-extrabold tracking-tight text-dwc-charcoal md:text-3xl">Why Shop With DWC?</h2>
      <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { icon: BadgeCheck, title: "DWC-Curated Products", text: "Every product answers one question: why would a driver need this?" },
          { icon: ShieldCheck, title: "Safety-First Selection", text: "We avoid products that encourage unsafe or distracted driving." },
          { icon: PackageSearch, title: "Clear Product Information", text: "Honest descriptions, real features — no exaggerated claims." },
          { icon: Headset, title: "DWC Customer Support", text: "The same support team that looks after our learners." },
        ].map((p) => (
          <div key={p.title} className="rounded-2xl border border-dwc-green/10 bg-white p-6">
            <p.icon className="h-6 w-6 text-dwc-green" aria-hidden="true" />
            <h3 className="mt-3 text-sm font-extrabold uppercase tracking-wide text-dwc-charcoal">{p.title}</h3>
            <p className="mt-1.5 text-[13px] leading-relaxed text-dwc-charcoal/65">{p.text}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const StoreToTrainingBand = () => {
  const { open } = useLeadModal();
  return (
    <section className="bg-dwc-green py-16 grain" data-testid="store-training-band">
      <div className="mx-auto flex max-w-7xl flex-col items-start gap-6 px-5 md:flex-row md:items-center md:justify-between lg:px-8">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-emerald-200">Learning To Drive?</p>
          <h2 className="mt-2 text-2xl font-extrabold tracking-tight text-white md:text-3xl">Build your driving skills and confidence with DWC.</h2>
        </div>
        <PrimaryButton onClick={() => open()} testId="store-book-demo-btn" className="bg-white !text-dwc-green hover:!bg-dwc-soft">
          Book an Appointment
        </PrimaryButton>
      </div>
    </section>
  );
};

export const HomeStorePromo = () => (
  <section className="bg-white py-16 md:py-20" data-testid="home-store-promo">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <Reveal>
        <div className="flex flex-col items-start gap-6 rounded-[2rem] border border-dwc-green/15 bg-dwc-soft p-8 md:flex-row md:items-center md:justify-between md:p-10">
          <div className="flex items-start gap-5">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-dwc-green text-white">
              <ShoppingBag className="h-7 w-7" aria-hidden="true" />
            </span>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.3em] text-dwc-crimson">DWC Store</p>
              <h2 className="mt-1 text-xl font-extrabold tracking-tight text-dwc-charcoal md:text-2xl">Drive Better. Drive Smarter.</h2>
              <p className="mt-1 text-sm text-dwc-charcoal/65">Driving essentials selected for everyday drivers.</p>
            </div>
          </div>
          <Link
            to="/store"
            data-testid="home-shop-store-btn"
            className="group inline-flex shrink-0 items-center gap-2 rounded-xl bg-dwc-green px-6 py-3.5 text-sm font-bold uppercase tracking-wide text-white transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-dwc-green-dark"
          >
            Shop DWC Store
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);
